import React, { useEffect, useState } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import SectionIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import ActionsIcon from '@mui/icons-material/ArticleOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandMoreIcon from '@mui/icons-material/ArrowDropDownOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorBadge from '../../../common/ErrorBadge';
import { NavigationGroup } from '../types';
import { ListingResource, PropertyField } from '../../../../system/field';
import { useFieldHidden } from '@brightmls/nextgen.lae.flc';
import { useMetadata } from '../../../../contexts/MetadataContext';
import { EXCLUDED_SEARCH_LOOKUPS } from '../../../common/SearchDropDown';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';
import { Buttons } from '../../../../constants/Buttons';
import SetFieldDefaults from '../SetFieldDefaults';
import { useModal } from '../../../../contexts/ModalContext';

interface MobileNavigationProps {
  groups: NavigationGroup[];
  activeGroup: string;
  activeSection: string;
  value: number;
  isSectionsDrawerOpen: boolean;
  isSavingDraft: boolean;
  isSearchMode: boolean;
  isResidental: boolean;
  isViewOnly: () => boolean;
  searchTerm: string;
  anchorEl: null | HTMLElement;
  filteredResults: any[];
  sectionErrors: { [key: string]: number };
  groupErrors: { [key: string]: number };
  setDefaultsStyles: SxProps;
  handleGroupClick: (
    groupName: string,
    fieldName?: string,
    isMobile?: boolean,
    followError?: boolean,
  ) => void;
  handleSectionClick: (sectionName: string, followError?: boolean) => void;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  handleSectionsDrawerToggle: () => void;
  handleCloseDrawer: () => void;
  handleSearchDrawerToggle: () => void;
  handleSearchClick: () => void;
  handleSearchChange: (value: string) => void;
  handleScrollToTop: () => void;
  handleSearchEntriesChange: (fields: PropertyField[] | null) => void;
  handlePublish: () => void;
  handleShareDraft: () => void;
  handleSaveDraft: () => void;
  handlePreviewDraft: () => void;
}

interface SearchFieldResult {
  type: 'field' | 'lookup';
  field: {
    key: string;
    label: string;
  };
  groupName: string;
  sectionName: string;
  lookupValue?: string;
  lookupKey?: string;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  groups,
  activeGroup,
  activeSection,
  isSectionsDrawerOpen,
  isSearchMode,
  searchTerm,
  anchorEl,
  filteredResults,
  sectionErrors,
  groupErrors,
  setDefaultsStyles,
  handleGroupClick,
  handleSectionClick,
  handleClick,
  handleClose,
  handleSectionsDrawerToggle,
  handleSearchDrawerToggle,
  handleCloseDrawer,
  handleSearchChange,
  handleScrollToTop,
  handleSearchEntriesChange,
  handleSaveDraft,
  handlePreviewDraft,
  handlePublish,
  handleShareDraft,
  isSavingDraft,
  isResidental,
  isViewOnly,
}) => {
  const { field, lookup } = useMetadata();
  const isFieldHidden = useFieldHidden();
  // Internal state for bottom navigation value
  const [selectedValue, setSelectedValue] = useState<number>(-1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { hasFeature } = useFeatureFlags();
  const { showModal } = useModal();

  const showPublishActions = hasFeature(FeatureFlags.NGW_PUBLISH);
  const showPublishOverride = hasFeature(FeatureFlags.NGW_PUBLISH_OVERRIDE);
  const hasSetFieldDefaults = hasFeature(FeatureFlags.NGW_EDIT_MEMBER_DEFAULTS);
  const hasSidepanelRedesign = hasFeature(
    FeatureFlags.NGW_EDIT_SIDEPANEL_REDESIGN,
  );

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    setSelectedValue(-1);
    handleClose(); // Original handleClose to clean up anchorEl
  };

  const onSaveDraft = () => {
    handleMenuClose();
    handleSaveDraft();
  };

  const onPreviewDraft = () => {
    handleMenuClose();
    handlePreviewDraft();
  };

  const onShareDraft = () => {
    handleMenuClose();
    handleShareDraft();
  };

  const errors = Object.values(sectionErrors).some((value) => value > 0);

  const handlePublishOverride = () => {
    if (errors) {
      showModal({
        title: 'Admin Publish Override',
        content:
          'As a NextGenWeb admin you may proceed to publish this listing even though there are form errors. Would you like to proceed?',
        confirmText: 'Proceed',
        cancelText: 'Cancel',
        onConfirm: handlePublish,
      });
    } else {
      handlePublish();
    }
  };

  // Reset selection when drawers are closed
  useEffect(() => {
    if (!isSectionsDrawerOpen) {
      setSelectedValue(-1);
    } else if (isSearchMode) {
      setSelectedValue(1);
    } else {
      setSelectedValue(0);
    }
  }, [isSectionsDrawerOpen, isSearchMode]);

  const handleNavigationChange = (newValue: number) => {
    if (newValue === 0) {
      setIsMenuOpen(false);
      // Sections
      if (!isSectionsDrawerOpen) {
        // Opening sections for the first time
        handleSectionsDrawerToggle();
      } else if (isSearchMode) {
        // Switching from search to sections - just disable search mode
        handleSectionsDrawerToggle();
      } else {
        // Closing sections
        handleSectionsDrawerToggle();
      }
    } else if (newValue === 1) {
      setIsMenuOpen(false);
      // Search
      if (!isSectionsDrawerOpen) {
        // Opening search for the first time
        handleSectionsDrawerToggle();
        handleSearchDrawerToggle();
      } else if (!isSearchMode) {
        // Switching from sections to search
        handleSearchDrawerToggle();
      } else {
        // Closing search
        handleSearchDrawerToggle();
        handleSectionsDrawerToggle();
      }
    } else if (newValue === 2) {
      // Actions
      if (isMenuOpen) {
        setIsMenuOpen(false);
        setSelectedValue(-1);
      } else {
        setIsMenuOpen(true);
        setSelectedValue(newValue);
        handleClick(new MouseEvent('click') as any);
      }
    } else if (newValue === 3) {
      // To Top
      setIsMenuOpen(false);
      setSelectedValue(-1);
      handleScrollToTop();
      handleCloseDrawer();
    }
  };

  const getSearchableFields = (): SearchFieldResult[] => {
    const searchableFields: SearchFieldResult[] = [];

    groups.forEach((group) => {
      group.sections.forEach((section) => {
        section.fields.forEach((fieldItem) => {
          if (
            fieldItem?.definition?.Searchable &&
            !isFieldHidden(
              ListingResource.Property,
              fieldItem.key as PropertyField,
            )
          ) {
            // Add the field itself
            searchableFields.push({
              type: 'field',
              field: fieldItem,
              groupName: group.name,
              sectionName: section.name,
            });

            // Add lookup values if available and not excluded
            const fieldMeta = field(fieldItem.key);
            if (
              fieldMeta?.Lookup &&
              !EXCLUDED_SEARCH_LOOKUPS.includes(fieldMeta.Lookup)
            ) {
              const lookupValues = lookup(fieldMeta.Lookup);
              if (lookupValues) {
                lookupValues.forEach((lookupValue) => {
                  searchableFields.push({
                    type: 'lookup',
                    field: fieldItem,
                    groupName: group.name,
                    sectionName: section.name,
                    lookupValue: lookupValue.LookupValue,
                    lookupKey: lookupValue.LookupKey,
                  });
                });
              }
            }
          }
        });
      });
    });

    return searchableFields.sort((a, b) => {
      if (a.type === 'field' && b.type === 'lookup') return -1;
      if (a.type === 'lookup' && b.type === 'field') return 1;
      return 0;
    });
  };

  const handleSearchTermChange = (value: string) => {
    handleSearchChange(value);

    if (value.length >= 3) {
      const searchableFields = getSearchableFields();
      const results = searchableFields.filter((result) => {
        const searchValue = value.toLowerCase();
        if (result.type === 'field') {
          return result.field.label.toLowerCase().includes(searchValue);
        } else {
          const searchText =
            `${result.lookupValue} (${result.field.label})`.toLowerCase();
          return searchText.includes(searchValue);
        }
      });

      if (handleSearchEntriesChange) {
        handleSearchEntriesChange(
          results.map((result) => result.field.key as PropertyField),
        );
      }
    }
  };

  useEffect(() => {
    if (searchTerm.length >= 3) {
      handleSearchEntriesChange(
        filteredResults.map((field) => field.fieldKey as PropertyField),
      );
    } else {
      handleSearchEntriesChange(null);
    }
  }, [searchTerm, filteredResults, handleSearchEntriesChange]);

  const SearchResultItem: React.FC<{
    result: SearchFieldResult;
    isFirstOfType: boolean;
  }> = ({ result, isFirstOfType: _isFirstOfType }) => {
    const handleResultClick = () => {
      handleGroupClick(result.groupName, result.field.key, true);
      handleSearchDrawerToggle();
    };

    return (
      <>
        <ListItem
          onClick={handleResultClick}
          sx={{
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <ListItemText
            primary={
              result.type === 'lookup' ? result.lookupValue : result.field.label
            }
            secondary={
              result.type === 'lookup' ? result.field.label : result.sectionName
            }
            primaryTypographyProps={{
              fontSize: '16px',
              fontWeight: result.type === 'field' ? 500 : 400,
            }}
            secondaryTypographyProps={{
              fontSize: '14px',
              color: 'text.secondary',
            }}
          />
        </ListItem>
      </>
    );
  };

  const renderSearchResults = () => {
    const searchableFields = getSearchableFields();
    const filteredFields = searchableFields.filter((result) => {
      const searchValue = searchTerm.toLowerCase();
      if (result.type === 'field') {
        return result.field.label.toLowerCase().includes(searchValue);
      } else {
        const searchText = `${result.lookupValue}`.toLowerCase();
        return searchText.includes(searchValue);
      }
    });

    return (
      <>
        {filteredFields.map((result, index) => (
          <SearchResultItem
            key={`${result.type}-${result.field.key}-${index}`}
            result={result}
            isFirstOfType={
              index === 0 || filteredFields[index - 1].type !== result.type
            }
          />
        ))}
      </>
    );
  };

  return (
    <>
      <Box
        data-testid="mobile-nav"
        sx={{
          width: '100%',
          position: 'fixed',
          bottom: 0,
          transition: 'bottom 0.3s ease',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '5px 8px',
          backgroundColor: 'grey.lightest',
          zIndex: 1005,
        }}
      >
        <BottomNavigation
          value={isSectionsDrawerOpen ? (isSearchMode ? 1 : 0) : selectedValue}
          onChange={(event, newValue) => handleNavigationChange(newValue)}
          showLabels
          sx={{
            backgroundColor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              color: 'secondary.medium',
              minWidth: '56px',
              maxWidth: '72px',
              padding: '6px 0',
              margin: '0 6px',
              borderRadius: '4px',
              '&.Mui-selected': {
                color: 'secondary.medium',
              },
              // Ensure font size stays consistent in all states
              '& .MuiBottomNavigationAction-label': {
                fontSize: '12px !important', // Force consistent size
                marginTop: '4px',
                fontWeight: '400',
                transition: 'none', // Prevent transition animations
              },
              // Ensure icon size stays consistent
              '& .MuiSvgIcon-root': {
                fontSize: '20px !important', // Force consistent size
                transition: 'none', // Prevent transition animations
              },
            },
            // Remove any other size-related transitions
            '& .MuiBottomNavigationAction-wrapper': {
              transition: 'background-color 0.2s ease', // Only transition the background
            },
          }}
        >
          <BottomNavigationAction
            data-testid="section-button"
            label="Section"
            icon={<SectionIcon />}
            onClick={handleSectionsDrawerToggle}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'background.secondary',
                border: 'none',
                color: 'secondary.medium',
              },
            }}
          />
          <BottomNavigationAction
            label="Search"
            icon={<SearchIcon data-testid="search-icon" />}
            onClick={handleSearchDrawerToggle}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'background.secondary',
                border: 'none',
                color: 'secondary.medium',
              },
            }}
          />
          {showPublishActions && (
            <BottomNavigationAction
              label="Actions"
              icon={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ActionsIcon />
                  <ExpandMoreIcon sx={{ fontSize: '16px', ml: 0.5 }} />
                </Box>
              }
              onClick={handleClick}
              sx={{
                '&.Mui-selected': {
                  border: '3px solid states.focus',
                  backgroundColor: 'transparent',
                  color: 'secondary.medium',
                },
              }}
            />
          )}
          <BottomNavigationAction
            label="To Top"
            icon={<ArrowUpwardIcon />}
            onClick={handleScrollToTop}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'background.secondary',
                border: 'none',
                color: 'secondary.medium',
              },
            }}
          />
        </BottomNavigation>

        {showPublishActions ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              className="bmls_btn"
              color="primary"
              size="medium"
              variant="contained"
              disabled={errors}
              onClick={handlePublish}
            >
              Publish
            </Button>
            {showPublishOverride && (
              <Button
                className="bmls_btn"
                color="primary"
                size="medium"
                variant="contained"
                onClick={handlePublishOverride}
              >
                Override
              </Button>
            )}
          </Box>
        ) : (
          !isViewOnly() && (
            <Button
              sx={Buttons.primary}
              disabled={isSavingDraft}
              onClick={handleSaveDraft}
              variant="contained"
            >
              {isSavingDraft && <CircularProgress size={20} sx={{ mr: 1 }} />}
              Save Draft
            </Button>
          )
        )}

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          sx={{
            zIndex: 10000,
          }}
        >
          {!isViewOnly() && (
            <MenuItem onClick={onSaveDraft}>Save Draft</MenuItem>
          )}
          {isResidental && hasFeature(FeatureFlags.NGW_PREVIEW_DRAFT) && (
            <MenuItem onClick={onPreviewDraft}>Preview Draft</MenuItem>
          )}
          {isResidental && hasFeature(FeatureFlags.NGW_SHARE_DRAFT) && (
            <MenuItem onClick={onShareDraft}>Share Draft</MenuItem>
          )}
        </Menu>
      </Box>
      <Drawer
        anchor="left"
        open={isSectionsDrawerOpen}
        onClose={
          isSearchMode ? handleSearchDrawerToggle : handleSectionsDrawerToggle
        }
        sx={{
          overflowY: 'hidden',
          '& .MuiDrawer-paper': {
            width: '100%',
            bgcolor: 'background.paper',
            zIndex: 1000, // Very low z-index for the drawer content
          },
          '& .MuiBackdrop-root': {
            zIndex: 1000, // Same low z-index for backdrop
          },
          zIndex: '1000 !important', // Force the drawer container itself to have low z-index
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '26px 16px 16px 16px',
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            backgroundColor: 'grey.50',
          }}
        >
          <Typography
            sx={{
              flexGrow: 1,
              color: 'grey.700',
              fontSize: '24px',
              fontWeight: '500',
            }}
          >
            {isSearchMode ? 'Search' : 'Listing Sections'}
          </Typography>
          <IconButton
            onClick={
              isSearchMode
                ? handleSearchDrawerToggle
                : handleSectionsDrawerToggle
            }
            sx={{ padding: 0 }}
          >
            <CloseIcon sx={{ color: 'grey.600' }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            overflowY: 'scroll',
            paddingBottom: hasSidepanelRedesign ? 0 : '80px', // prevents SFD button from covering bottom of sections list
            height:
              hasSetFieldDefaults && hasSidepanelRedesign
                ? 'calc(100% - 160px)' // 80 for set defaults footer, 80 for header
                : '100%',
          }}
        >
          {isSearchMode ? (
            <Box sx={{ padding: '16px 0' }} data-testid="search-screen">
              <Box sx={{ padding: '0 20px' }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Find a Field or Value"
                  value={searchTerm}
                  onChange={(e) => handleSearchTermChange(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" />,
                    endAdornment: searchTerm && (
                      <IconButton
                        onClick={() => handleSearchTermChange('')}
                        edge="end"
                      >
                        <CloseIcon sx={{ height: '16px', width: '16px' }} />
                      </IconButton>
                    ),
                  }}
                  sx={{ backgroundColor: 'white' }}
                />
              </Box>

              {searchTerm.length > 2 && (
                <List sx={{ mt: 2, p: 0 }}>{renderSearchResults()}</List>
              )}
            </Box>
          ) : (
            <List data-testid="section-screen">
              {groups.map((group, index) => (
                <React.Fragment key={index}>
                  <ListItem disablePadding>
                    <ListItemText
                      primary={group.name}
                      onClick={() =>
                        handleGroupClick(group.name, undefined, false, false)
                      }
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: '700',
                          fontSize: '14px',
                          color: 'grey.700',
                          padding: '12px 16px',
                        },
                      }}
                    />
                    {activeGroup !== group.name &&
                      groupErrors[group.name] > 0 && (
                        <ErrorBadge
                          count={groupErrors[group.name]}
                          onClick={() =>
                            handleGroupClick(group.name, undefined, true, true)
                          }
                        />
                      )}
                  </ListItem>
                  {activeGroup === group.name && (
                    <Box sx={{ marginTop: '8px' }}>
                      {group.sections.map((section, _sectionIndex) => (
                        <Box
                          key={section.name}
                          onClick={() => {
                            handleSectionClick(section.name, false);
                            handleSectionsDrawerToggle();
                          }}
                          sx={{
                            borderLeft:
                              activeSection === section.name
                                ? '6px solid'
                                : '6px solid transparent',
                            borderLeftColor:
                              activeSection === section.name
                                ? 'secondary.dark'
                                : 'transparent',
                            backgroundColor:
                              activeSection === section.name
                                ? 'action.hover'
                                : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 20px',
                            cursor: 'pointer',
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'grey.700',
                              fontSize: '14px',
                              fontWeight: '400',
                              flexGrow: 1,
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                          >
                            {section.name}
                          </Typography>
                          {sectionErrors[section.name] > 0 && (
                            <Box sx={{ marginLeft: '8px' }}>
                              <ErrorBadge
                                count={sectionErrors[section.name]}
                                onClick={() =>
                                  handleSectionClick(section.name, true)
                                }
                              />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
        {hasSetFieldDefaults && (
          <SetFieldDefaults
            outlinedIcon={true}
            content={
              <Typography fontWeight={hasSidepanelRedesign ? 'normal' : 'bold'}>
                Set Field Defaults
              </Typography>
            }
            styles={setDefaultsStyles}
            isMobile={true}
          />
        )}
      </Drawer>
    </>
  );
};

export default MobileNavigation;
